import React, { useContext, useMemo } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Label } from '@fluentui/react';
import { ExistingBrandsPicker } from './existing-brands-picker';
import { SelectedBrandsTags } from './selected-brands-tags';
import { RequestBrandButton } from './request-brand-button';
import { useBrandsData } from './brands-data';

const getStyles = (_components, { palette }) => ({
  SelectedExistingBrandsContainer: {
    '& .ms-MessageBar': {
      marginBottom: 16,
    },
  },
  SelectedNewBrandsContainer: {
    marginTop: 16,
    '& p': {
      margin: 0,
    },
    '& hr': {
      background: palette.neutralLight,
      border: 'none',
      height: 1,
    },
    '& .request-brand-button': {
      marginTop: 8,
    },
  },
});

export const BrandsPicker = ({
  onRequestBrand,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);

  const {
    selectedOptions,
    setSelectedOptions,
  } = useBrandsData();

  // The approved brands
  const approvedBrands = useMemo(() => selectedOptions.filter(brand => !brand.isUnderReview), [selectedOptions]);

  const onRemoveAllApprovedBrands = () => {
    const filteredBrands = selectedOptions.filter(brand => brand.isUnderReview);
    setSelectedOptions(filteredBrands);
  };

  const onRemoveOneApprovedBrand = (brand) => {
    const filteredBrands = selectedOptions.filter(selectedBrand => selectedBrand.key !== brand.key);
    setSelectedOptions(filteredBrands);
  };

  // The pending brands
  const pendingBrands = useMemo(() => selectedOptions.filter(brand => brand.isUnderReview), [selectedOptions]);

  const onRemoveAllPendingBrands = () => {
    const filteredBrands = selectedOptions.filter(brand => !brand.isUnderReview);
    setSelectedOptions(filteredBrands);
  };

  const onRemoveOnePendingBrand = (brand) => {
    const filteredBrands = selectedOptions.filter(selectedBrand => selectedBrand.key !== brand.key);
    setSelectedOptions(filteredBrands);
  };

  return (
    <>
      <div className={classes.SelectedExistingBrandsContainer}>
        <ExistingBrandsPicker onRequestBrand={onRequestBrand} />
        <SelectedBrandsTags
          className={classNames(classes.SelectedExistingBrandsTags, 'selected-existing-brands-tags')}
          selectedOptions={approvedBrands}
          onRemoveAll={onRemoveAllApprovedBrands}
          onRemoveItem={onRemoveOneApprovedBrand}
          isAwaysDisplayHeader={!_.isEmpty(pendingBrands)}
        />
      </div>
      <div className={classes.SelectedNewBrandsContainer}>
        {!_.isEmpty(pendingBrands) && (
          <>
            <hr />
            <Label htmlFor="requested_brands">
              {i18n.getString(_TL_('Requested brands under review'))}
            </Label>
            <p id="requested_brands">
              {i18n.getString(_TL_('Once approved, we’ll automatically add these brands to your list.'))}
            </p>
            <SelectedBrandsTags
              className={classNames(classes.SelectedNewBrandsTags, 'selected-new-brands-tags')}
              selectedOptions={pendingBrands}
              onRemoveAll={onRemoveAllPendingBrands}
              onRemoveItem={onRemoveOnePendingBrand}
            />
          </>
        )}
        <RequestBrandButton onClick={onRequestBrand} />
      </div>
    </>
  );
};

BrandsPicker.propTypes = {
  onRequestBrand: PropTypes.func.isRequired,
};
